import { useContext, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCupom } from "../../hooks/useCupom";
import { Cupom } from "../../services/onboarding";
import { Button, Input } from "@nextui-org/react";
import { BiLoaderAlt } from "react-icons/bi";
import { FaRegCircleCheck } from "react-icons/fa6";
import { formatToBRL } from "../../utils/formatToBRL";
import { SessionContext } from "../../context/session";

type Feedback = {
  type: "success" | "error";
  message: string;
};

const cupomFormSchema = z.object({
  codigo: z
    .string()
    .trim()
    .nonempty("Campo Obrigatório.")
    .min(3, "Deve ter no mínimo 3 caracteres.")
    .max(20, "Deve ter no máximo 20 caracteres."),
});

type CupomFormSchema = z.infer<typeof cupomFormSchema>;

function maskUppercase(value: string) {
  return value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
}

const CupomComponent = () => {
  const { setItemSession } = useContext(SessionContext);

  const sessionItemData = sessionStorage.getItem("appliedCupom");
  const appliedCupom = sessionItemData ? (JSON.parse(sessionItemData || "{}") as Cupom) : null;

  const [feedback, setFeedback] = useState<Feedback | null>(() => {
    if (appliedCupom) return { type: "success", message: `Cupom de ${formatToBRL(appliedCupom.valor)} aplicado!` };
    return null;
  });

  const isApplied = feedback?.type === "success" || !!appliedCupom;
  const hasError = feedback?.type === "error";

  const form = useForm<CupomFormSchema>({
    resolver: zodResolver(cupomFormSchema),
    defaultValues: { codigo: appliedCupom?.codigo || "" },
    mode: "all",
  });


  const { mutate, isPending } = useCupom({
    onSuccess: (response) => {
      if ("data" in response) {
        setFeedback({ type: "success", message: `Cupom de ${formatToBRL(response.data.valor)} aplicado!` });
        setItemSession("appliedCupom", JSON.stringify(response.data));
        return;
      }

      setFeedback({ type: "error", message: response.message });
    },
    onError: () => {
      setFeedback({ type: "error", message: "Cupom Inválido" });
    },
  });

  const handleApply = form.handleSubmit((data) => {
    setFeedback(null);
    mutate({ codigo: data.codigo });
  });

  return (
    <div className="flex flex-col gap-1.5 font-kumbh-sans relative mb-6">
      <div className="flex gap-2 items-center">
        <Controller
          name="codigo"
          control={form.control}
          render={({ field }) => (
            <Input
              label="Insira o código do cupom"
              isDisabled={isPending || isApplied}
              data-has-applied={isApplied}
              {...field}
              onChange={(e) => {
                field.onChange(maskUppercase(e.target.value));
                setFeedback(null);
              }}
              onKeyDown={(e) => e.key === "Enter" && handleApply()}
              classNames={{
                base: ["data-[disabled=true]:opacity-80"],
                label: ["text-normal", "group-data-[invalid=true]:!text-[#d0342c]"],
                inputWrapper: ["bg-white min-h-[56px] data-[hover=true]:bg-white group-data-[focus=true]:bg-white"],
                input: ["text-base", "group-data-[invalid=true]:!text-[#d0342c]"],
              }}
              isInvalid={hasError}
              size="md"
            />
          )}
        />
        <Button
          className="whitespace-nowrap rounded-xl bg-[var(--main-color)]  text-sm font-bold tracking-wide text-[var(--continue-button-font)] transition-all duration-400 disabled:cursor-not-allowed disabled:opacity-60 min-h-[50px] px-7 w-[96px]"
          onClick={handleApply}
          disabled={isPending || isApplied || !form.formState.isValid || feedback?.type === "error"}
        >
          {isPending ? <BiLoaderAlt size={32} className="animate-spin" /> : isApplied ? <FaRegCircleCheck size={24} /> : "Aplicar"}
        </Button>
      </div>

      <span
        data-feedback-type={feedback?.type}
        className="block w-full text-xs ml-3 absolute -bottom-5 data-[feedback-type=success]:!text-[#1b8a3d] data-[feedback-type=error]:!text-[#d0342c] data-[feedback-type='success']:mx-auto data-[feedback-type='success']:font-bold data-[feedback-type='success']:text-center data-[feedback-type='success']:text-base data-[feedback-type='success']:-bottom-7"
      >
        {feedback?.message}
      </span>
    </div>
  );
};

export default CupomComponent;
